import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import { UserTable } from '../admin/userManagement/UserTable'
import RequireAdmin from './RequireAdmin'
import JobSummary from '../pages/job-summary/JobSummary'
import CustomerListing from '../pages/customers/CustomerListing'
import AccountingPage from '../pages/accounting/AccountingPage'
import CashFlowPage from '../pages/cashflow/CashFlowPage'
import BankingPage from '../pages/banking/BankingPage'
import CalendarPage from '../pages/dashboard/calendar/CalendarPage'
import ClientFocus from '../pages/dashboard/ClientFocus'
import CustomerActivityPage from '../pages/customer-activity/CustomerActivityPage'
import { BudgetPage } from '../pages/budget/BudgetPage'
import AnalyticsPage from '../pages/analytics/Analytics'
import FileExplorerPage from '../pages/file-explorer/FileExplorerPage'
import GoogleAdsPage from '../pages/marketing/GoogleAdsPage'
import ClientsPage from '../pages/clients/ClientsPage'

import { EmailPanelProvider } from '../pages/mail/contexts/EmailPanelContext'
import EmailSlidingPanel from '../pages/mail/components/EmailSlidingPanel'
import { AppDataProvider } from '../providers/AppDataProvider'
import AppLoadingScreen from '../providers/AppLoadingScreen'
import { WebsiteLeadsProvider } from '../contexts/WebsiteLeadsContext'
import { StripePaymentsProvider } from '../contexts/StripePaymentsContext'
import { SubscriptionsProvider } from '../contexts/SubscriptionsContext'
import WebsiteLeadsPanel from '../pages/leads/WebsiteLeadsPanel'
import WebsiteSubmissionsPage from '../pages/leads/WebsiteSubmissionsPage'
import RecurringPaymentsPage from '../pages/recurring/RecurringPaymentsPage'
const MonthlySupportPage = lazy(() => import('../pages/monthly-support/MonthlySupportPage'))
const MassEmailPage = lazy(() => import('../pages/mass-email/MassEmailPage'))

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))




  return (
    <AppDataProvider>
      <AppLoadingScreen>
        <WebsiteLeadsProvider>
        <StripePaymentsProvider>
        <SubscriptionsProvider>
        <EmailPanelProvider>
            <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registration */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        <Route
          path='customer-activity'
          element={
            <SuspensedView>
              <CustomerActivityPage />
            </SuspensedView>
          }
        />
<Route
      path='clientfocus'
      element={
        <SuspensedView>   
          <ClientFocus />
        </SuspensedView>
      }
    />
        
        {/* Lazy Modules */}
        <Route
          path='user/pages/profile'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        <Route
          path='user/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='budget'
          element={
            <SuspensedView>
              <BudgetPage />
            </SuspensedView>
          }
        />
        <Route
          path='analytics'
          element={
            <SuspensedView>
              <AnalyticsPage />
            </SuspensedView>
          }
        />
        <Route
          path='file-explorer'
          element={
            <SuspensedView>
              <FileExplorerPage />
            </SuspensedView>
          }
        />
        <Route
          path='marketing/google-ads'
          element={
            <SuspensedView>
              <GoogleAdsPage />
            </SuspensedView>
          }
        />
        <Route path='website-submissions' element={<WebsiteSubmissionsPage />} />
        <Route path='recurring-payments' element={<RecurringPaymentsPage />} />
        <Route path='monthly-support' element={<SuspensedView><MonthlySupportPage /></SuspensedView>} />
        {/* Deliberately NOT wrapped in <RequireAdmin>: that guard checks an `isAdmin` token
            claim no account has (admin is `role` on users/{uid}, which is what firestore.rules
            read), so it would bounce everyone to /dashboard. The server enforces instead:
            campaign reads need users.role=='admin', sends/pauses/opt-outs need the owner. */}
        <Route path='marketing/mass-email' element={<SuspensedView><MassEmailPage /></SuspensedView>} />

        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        {/* Job Tracking Components */}

        <Route
      path='jobtracking/summary'
      element={
        <SuspensedView>
          <JobSummary />
        </SuspensedView>
      }
    />

{/* The calendar. It used to exist ONLY as a modal, so it could not be linked
    to, bookmarked, refreshed or pointed at by Andrei/notifications, and Back
    did not close it. `?date=YYYY-MM-DD&view=month|week|agenda` drives it. */}
<Route
      path='calendar'
      element={
        <SuspensedView>
          <CalendarPage />
        </SuspensedView>
      }
    />

{/* `jobtracking/events` was a deprecated read-only table (N+1 reads, raw ISO
    output, mismatched columns) and the only route-addressable events surface.
    Redirected rather than removed so existing links and bookmarks still land
    somewhere sensible. */}
<Route path='jobtracking/events' element={<Navigate to='/calendar' replace />} />

        



        {/* CRM Components */}

        <Route
          path='contacts'
          element={
            <SuspensedView>
              <ClientsPage />
            </SuspensedView>
          }
        />

        {/* Legacy route redirects */}
        <Route path='customers/list' element={<Navigate to='/contacts' replace />} />
        <Route path='clients' element={<Navigate to='/contacts' replace />} />

        <Route
          path='accounting'
          element={
            <SuspensedView>
              <AccountingPage />
            </SuspensedView>
          }
        />
        <Route
          path='accounting/cashflow'
          element={
            <SuspensedView>
              <CashFlowPage />
            </SuspensedView>
          }
        />
        {/* Banking / Reconciliation */}
        <Route
          path='banking'
          element={
            <SuspensedView>
              <BankingPage />
            </SuspensedView>
          }
        />

        {/* Redirects for backward compatibility */}
        <Route path='invoices' element={<Navigate to='/accounting?view=invoices' replace />} />
        <Route path='bills' element={<Navigate to='/accounting?view=bills' replace />} />

        
        {/* Admin User Management */}

        <Route
      path='/admin/usermanagement/users'
      element={
        <RequireAdmin>
          <SuspensedView>
            <UserTable />
          </SuspensedView>
        </RequireAdmin>
      }
    />

<Route
      path='/admin/usermanagement/userspage'
      element={
        <RequireAdmin>
          <SuspensedView>
            <UsersPage />
          </SuspensedView>
        </RequireAdmin>
      }
    />

        {/* END Admin User Management */}



        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
            </Routes>
            <EmailSlidingPanel />
            <WebsiteLeadsPanel />
        </EmailPanelProvider>
        </SubscriptionsProvider>
        </StripePaymentsProvider>
        </WebsiteLeadsProvider>
      </AppLoadingScreen>
    </AppDataProvider>
  )
}

export {PrivateRoutes}
